import React, { useState, useEffect, useContext } from 'react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'

const ReportInfringement = () => {
  const { getMyInfringingUrls, addInfringingUrls, isLoading } = useContext(AppContext)
  const [urls, setUrls] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittedUrls, setSubmittedUrls] = useState([])
  const [isValidUrls, setIsValidUrls] = useState(true)

  useEffect(() => {
    fetchSubmittedUrls()
  }, [])

  // URL validation regex
  const urlRegex =
    /^https?:\/\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?::[0-9]+)?\/[^\s]+(?:\?[^\s]*)?(?:#[^\s]*)?$/i

  const validateUrls = (urlText) => {
    if (!urlText.trim()) return true

    const urlLines = urlText
      .trim()
      .split('\n')
      .filter((line) => line.trim())

    if (urlLines.length === 0) return true

    return urlLines.every((line) => {
      const trimmedLine = line.trim()
      return trimmedLine === '' || urlRegex.test(trimmedLine)
    })
  }

  const handleUrlChange = (e) => {
    const newUrls = e.target.value
    setUrls(newUrls)
    setIsValidUrls(validateUrls(newUrls))
  }

  const fetchSubmittedUrls = async () => {
    try {
      const urlsList = await getMyInfringingUrls()
      setSubmittedUrls(urlsList)
    } catch (error) {
      console.error('Failed to fetch submitted URLs:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!urls.trim()) return

    setIsSubmitting(true)
    try {
      const dedupedUrls = [
        ...new Set(
          urls
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line),
        ),
      ].join('\n')

      const result = await addInfringingUrls(dedupedUrls)
      if (result) {
        setUrls('')
        fetchSubmittedUrls() // Refresh the list

        // Show feedback about duplicates if any
      }
    } catch (error) {
      console.error('Error submitting URLs:', error)
      alert('Error submitting URLs. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AppLayout pageName='Report Infringement'>
      <div className='max-w-[95%] mx-auto p-6 space-y-6'>
        {/* First Card - Submit URLs */}
        <div className='bg-white rounded-lg shadow-md p-8'>
          <h2 className='text-2xl font-bold text-gray-900 mb-6'>Report Infringements</h2>

          <div className='mb-4'>
            <h3 className='text-lg font-semibold text-gray-800 mb-3'>Infringing URLs</h3>
            <textarea
              value={urls}
              onChange={handleUrlChange}
              placeholder='Please report copyright infringements by entering the URLs in rows.'
              className='w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none'
              rows={10}
            />
            {!isValidUrls && urls.trim() && (
              <p className='text-red-600 text-sm mt-2'>
                Please enter valid absolute URLs. Each absolute URL should start with http:// or https:// and be on a
                separate line.
                <br />
                Pure domain URLs (i.e https://www.example.com) are not allowed.
              </p>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !urls.trim() || !isValidUrls}
            className='bg-blue-600 text-white px-8 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>

        {/* Second Card - Previously Submitted URLs */}
        <div className='bg-white rounded-lg shadow-md p-8'>
          <h2 className='text-2xl font-bold text-gray-900 mb-6'>Previously Submitted URLs</h2>

          {isLoading ? (
            <div className='text-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto'></div>
              <p className='mt-2 text-gray-600'>Loading...</p>
            </div>
          ) : submittedUrls.length > 0 ? (
            <div className='overflow-x-auto'>
              <table className='min-w-full border-collapse border border-gray-300'>
                <thead>
                  <tr className='bg-gray-50'>
                    <th className='border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700'>URL</th>
                    <th className='border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700'>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {submittedUrls.map((item, index) => (
                    <tr
                      key={index}
                      className='hover:bg-gray-50'
                    >
                      <td className='border border-gray-300 px-4 py-3 text-gray-800'>
                        <a
                          href={item.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-blue-600 hover:underline break-all'
                        >
                          {item.url}
                        </a>
                      </td>

                      <td className='border border-gray-300 px-4 py-3 text-gray-600'>
                        {new Date(item.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-center py-8 text-gray-500'>
              <p>No URLs have been submitted yet.</p>
            </div>
          )}

          {/* Show total count */}
          {submittedUrls.length > 0 && (
            <div className='mt-4 text-sm text-gray-600'>
              Total: {submittedUrls.length} URL{submittedUrls.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}

export default ReportInfringement

